'use client';

import { useCallback, useState } from 'react';

import { Building2 } from 'lucide-react';

import { useSignInWithSSO } from '@kit/supabase/hooks/use-sign-in-with-sso';
import { Button } from '@kit/ui/button';
import { If } from '@kit/ui/if';
import { Input } from '@kit/ui/input';
import { LoadingOverlay } from '@kit/ui/loading-overlay';
import { Trans } from '@kit/ui/trans';

import { useLastAuthMethod } from '../hooks/use-last-auth-method';
import { AuthErrorAlert } from './auth-error-alert';

export function SSOSignInContainer(props: {
  paths: {
    callback: string;
    returnPath: string;
  };
}) {
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [email, setEmail] = useState('');
  const signInWithSSO = useSignInWithSSO();
  const { recordAuthMethod } = useLastAuthMethod();

  const loading = signInWithSSO.isPending;

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      const domain = email.split('@')[1];

      if (!domain) return;

      const origin = window.location.origin;
      const queryParams = new URLSearchParams();

      if (props.paths.returnPath) {
        queryParams.set('next', props.paths.returnPath);
      }

      const qs = queryParams.toString();
      const redirectPath = qs
        ? `${props.paths.callback}?${qs}`
        : props.paths.callback;

      const redirectTo = [origin, redirectPath].join('');

      signInWithSSO.mutate(
        { domain, redirectTo },
        {
          onSuccess: () => {
            recordAuthMethod('sso');
          },
        },
      );
    },
    [email, props.paths, signInWithSSO, recordAuthMethod],
  );

  if (!showEmailInput) {
    return (
      <Button
        variant="outline"
        className="flex w-full gap-x-3"
        data-test="sso-sign-in-button"
        onClick={() => setShowEmailInput(true)}
      >
        <Building2 className="h-4 w-4" />

        <span>
          <Trans i18nKey="auth.signInWithSSO" />
        </span>
      </Button>
    );
  }

  return (
    <div className="space-y-3">
      <If condition={loading}>
        <LoadingOverlay />
      </If>

      <form onSubmit={onSubmit} className="space-y-3">
        <Input
          data-test="sso-email-input"
          required
          type="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoFocus
        />

        <Button type="submit" className="w-full" data-test="sso-submit-button">
          <Trans i18nKey="auth.continueWithSSO" />
        </Button>
      </form>

      <Button
        variant="ghost"
        size="sm"
        className="w-full"
        data-test="sso-back-button"
        onClick={() => {
          setShowEmailInput(false);
          setEmail('');
          signInWithSSO.reset();
        }}
      >
        <Trans i18nKey="auth.backToSignIn" />
      </Button>

      <AuthErrorAlert error={signInWithSSO.error} />
    </div>
  );
}
